import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import CountryList from 'country-list-with-dial-code-and-flag';

import css from './FieldCountryCodes.module.css';

function buildCountriesList(rawList) {
  const raw = Array.isArray(rawList) ? rawList : [];
  const flat = raw.map(x => (x && x.data ? x.data : x)).filter(Boolean);
  const map = new Map();

  for (const c of flat) {
    const code = String(c.code || '')
      .toUpperCase()
      .trim();
    const name = String(c.name || '').trim();
    const dial = String(c.dial_code || c.dialCode || '')
      .trim()
      .replace(/\s+/g, '');

    if (!code || !name || !dial) continue;

    // Deduplicate by country + dial to avoid duplicates from the source list
    const dedupeKey = `${code}|${dial}`;
    if (!map.has(dedupeKey)) {
      map.set(dedupeKey, {
        ...c,
        code,
        name,
        dial_code: dial,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

function FieldCountryCodesComponent(props) {
  const { input, meta, id, label, placeholder, className, defaultCountryCode } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const allCountries = useMemo(() => {
    const raw = typeof CountryList?.getAll === 'function' ? CountryList.getAll() : CountryList;
    return buildCountriesList(raw);
  }, []);

  // Parse the stored value. Expected formats:
  // - "MX|+52" (preferred)
  // - "+52" (legacy)
  // - "MX" (edge/legacy)
  const parsedValue = useMemo(() => {
    const v = input.value;

    if (!v || typeof v !== 'string') return { code: null, dial: null };

    if (v.includes('|')) {
      const [code, dial] = v.split('|');
      return { code: (code || '').toUpperCase().trim(), dial: (dial || '').trim() };
    }

    // Legacy: only dial stored
    if (v.startsWith('+')) return { code: null, dial: v.trim() };

    // Legacy: only code stored
    return { code: v.toUpperCase().trim(), dial: null };
  }, [input.value]);

  const selectedCountry = useMemo(() => {
    // If there is no value in the form yet, use default country for UI
    if (!input.value) {
      return allCountries.find(c => c.code === defaultCountryCode) || allCountries[0];
    }

    // Preferred: match by BOTH code and dial when possible
    if (parsedValue.code && parsedValue.dial) {
      return (
        allCountries.find(c => c.code === parsedValue.code && c.dial_code === parsedValue.dial) ||
        allCountries[0]
      );
    }

    // Legacy: match by dial only
    if (parsedValue.dial) {
      return allCountries.find(c => c.dial_code === parsedValue.dial) || allCountries[0];
    }

    // Legacy: match by code only
    if (parsedValue.code) {
      return allCountries.find(c => c.code === parsedValue.code) || allCountries[0];
    }

    return allCountries[0];
  }, [input.value, parsedValue, allCountries, defaultCountryCode]);

  useEffect(() => {
    // Set a real default value in the form on first load
    if (!input.value && allCountries?.length) {
      const defaultCountry =
        allCountries.find(c => c.code === defaultCountryCode) || allCountries[0];

      if (defaultCountry?.code && defaultCountry?.dial_code) {
        // Store "CODE|DIAL" as the value
        input.onChange(`${defaultCountry.code}|${defaultCountry.dial_code}`);
      }
    }
  }, [input.value, allCountries, input, defaultCountryCode]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allCountries;

    return allCountries.filter(c => {
      const name = (c.name || '').toLowerCase();
      const code = (c.code || '').toLowerCase();
      const dial = (c.dial_code || '').toLowerCase();
      const local = (c.local_name || '').toLowerCase();
      return name.includes(q) || code.includes(q) || dial.includes(q) || local.includes(q);
    });
  }, [allCountries, searchTerm]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onPick = useCallback(
    c => {
      // Store "CODE|DIAL" in the form value
      input.onChange(`${c.code}|${c.dial_code}`);
      setIsOpen(false);
      setSearchTerm('');
    },
    [input]
  );

  const showError = meta.touched && meta.error;

  return (
    <div className={classNames(css.root, className)}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}

      <div className={css.countrySelector} ref={dropdownRef}>
        <button type="button" className={css.countryButton} onClick={() => setIsOpen(!isOpen)}>
          <span className={css.flagEmoji}>{selectedCountry?.flag || '🌍'}</span>
          <span className={css.dialCode}>{selectedCountry?.dial_code || ''}</span>
          <svg className={css.chevron} width="12" height="12" viewBox="0 0 12 12">
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
          </svg>
        </button>

        {isOpen && (
          <div className={css.dropdown}>
            <div className={css.searchWrapper}>
              <input
                type="text"
                className={css.searchInput}
                placeholder={placeholder || 'Search country...'}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            <ul className={css.countryList}>
              {filtered.length > 0 ? (
                filtered.map(c => (
                  <li
                    key={`${c.code}-${c.dial_code}`}
                    className={classNames(css.countryItem, {
                      [css.countryItemSelected]:
                        c.code === selectedCountry?.code &&
                        c.dial_code === selectedCountry?.dial_code,
                    })}
                    onClick={() => onPick(c)}
                  >
                    <span className={css.flagEmojiSmall}>{c.flag}</span>
                    <div className={css.countryInfo}>
                      <span className={css.countryNameItem}>{c.name}</span>
                    </div>
                    <span className={css.dialCodeSmall}>({c.dial_code})</span>
                  </li>
                ))
              ) : (
                <li className={css.noResults}>No countries found</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {showError ? <div className={css.error}>{meta.error}</div> : null}
    </div>
  );
}

FieldCountryCodesComponent.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  defaultCountryCode: PropTypes.string,
};

FieldCountryCodesComponent.defaultProps = {
  defaultCountryCode: 'IT',
};

export default function FieldCountryCodes(props) {
  const {
    name,
    id,
    label,
    placeholder,
    className,
    validate,
    userTypeConfig,
    defaultCountryCode,
  } = props;

  const { displayInSignUp } = userTypeConfig?.phoneNumberSettings || {};
  const isDisabled = userTypeConfig?.defaultUserFields?.phoneNumber === false;
  const isAllowedInSignUp = displayInSignUp === true;

  if (isDisabled || !isAllowedInSignUp) {
    return null;
  }

  return (
    <Field
      name={name}
      validate={validate}
      render={fieldRenderProps => (
        <FieldCountryCodesComponent
          {...fieldRenderProps}
          id={id || name}
          label={label}
          placeholder={placeholder}
          className={className}
          defaultCountryCode={defaultCountryCode}
        />
      )}
    />
  );
}

FieldCountryCodes.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  validate: PropTypes.func,
  userTypeConfig: PropTypes.object,
  defaultCountryCode: PropTypes.string,
};

FieldCountryCodes.defaultProps = {
  defaultCountryCode: 'IT',
};
